/**
 * Restaurant Settings Manager
 * Loads and displays dynamic restaurant settings from backend API with localStorage caching
 */

(function() {
  'use strict';

  const API_BASE = window.VEDHACART_CONFIG?.serverUrl || '';
  const STORAGE_KEY = 'restaurant_settings';
  const LAST_UPDATED_KEY = 'restaurant_settings_updated';
  let cachedSettings = null;

  /**
   * Save settings to localStorage
   */
  function saveToLocalStorage(settings) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
      localStorage.setItem(LAST_UPDATED_KEY, Date.now().toString());
    } catch (error) {
      console.error('Failed to save to localStorage:', error);
    }
  }

  /**
   * Load settings from localStorage
   */
  function loadFromLocalStorage() {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const settings = JSON.parse(stored);
        return settings;
      }
    } catch (error) {
      console.error('Failed to load from localStorage:', error);
    }
    return null;
  }

  /**
   * Fetch restaurant settings from API
   */
  async function fetchSettingsFromAPI() {
    try {
      // Add timeout to prevent long waits
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout

      const response = await fetch(API_BASE + '/api/proxy/settings', {
        signal: controller.signal
      });
      clearTimeout(timeoutId);

      const data = await response.json();

      if (data.success) {
        return data.settings;
      } else {
        console.error('Failed to load restaurant settings:', data.error);
        return null;
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        console.error('Restaurant settings request timed out after 5 seconds');
      } else {
        console.error('Error fetching restaurant settings:', error);
      }
      return null;
    }
  }

  /**
   * Check if settings have changed
   */
  function settingsHaveChanged(oldSettings, newSettings) {
    if (!oldSettings || !newSettings) return true;

    // Deep comparison of settings objects
    return JSON.stringify(oldSettings) !== JSON.stringify(newSettings);
  }

  /**
   * Fetch settings with localStorage caching
   * - First visit: Load from API, save to localStorage
   * - Subsequent visits: Load from localStorage immediately, sync with API in background
   */
  async function fetchSettings(forceRefresh = false) {
    // If already in memory cache, return it
    if (cachedSettings && !forceRefresh) {
      return cachedSettings;
    }

    // Try to load from localStorage first for instant display
    const localSettings = loadFromLocalStorage();
    if (localSettings && !forceRefresh) {
      cachedSettings = localSettings;

      // Sync with server in background to check for updates
      fetchSettingsFromAPI().then(serverSettings => {
        if (serverSettings && settingsHaveChanged(localSettings, serverSettings)) {
          cachedSettings = serverSettings;
          saveToLocalStorage(serverSettings);
          renderAllSettings();
        }
      });

      return localSettings;
    }

    // No localStorage cache - fetch from server
    const serverSettings = await fetchSettingsFromAPI();

    if (serverSettings) {
      cachedSettings = serverSettings;
      saveToLocalStorage(serverSettings);
      return serverSettings;
    }

    return null;
  }

  /**
   * Re-render all settings (called when background sync detects changes)
   */
  async function renderAllSettings() {
    await Promise.all([
      updateBusinessHours(),
      updateAcceptingOrders(),
      updateBusinessHoursStatus(),
      updateDeliveryInfo(),
      updateDeliveryZones(),
      updatePromotionalBanner(),
      updateMenuPromotionalBanner(),
      updateContactInfo()
    ]);
  }

  /**
   * Update business hours display
   */
  async function updateBusinessHours() {
    const settings = await fetchSettings();
    if (!settings || !settings.business_hours) return;

    const hoursContainer = document.getElementById('business-hours-display');
    if (!hoursContainer) return;

    const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

    let hoursHTML = '<div class="business-hours">';
    days.forEach((day, index) => {
      const hours = settings.business_hours[day];
      if (hours) {
        const status = hours.closed ? 'Closed' : `${hours.open} - ${hours.close}`;
        hoursHTML += `
          <div class="business-hours__item">
            <span class="business-hours__day">${dayNames[index]}</span>
            <span class="business-hours__time">${status}</span>
          </div>
        `;
      }
    });
    hoursHTML += '</div>';

    hoursContainer.innerHTML = hoursHTML;
  }

  /**
   * Get current day's business hours and calculate open/close status
   */
  function getBusinessHoursStatus(businessHours) {
    if (!businessHours) return null;

    const now = new Date();
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const currentDay = days[now.getDay()];
    const currentTime = now.getHours() * 60 + now.getMinutes(); // minutes since midnight

    const todayHours = businessHours[currentDay];
    if (!todayHours || todayHours.closed) {
      // Find next open day
      for (let i = 1; i <= 7; i++) {
        const nextDayIndex = (now.getDay() + i) % 7;
        const nextDay = days[nextDayIndex];
        const nextDayHours = businessHours[nextDay];

        if (nextDayHours && !nextDayHours.closed) {
          return {
            isOpen: false,
            message: `Opening ${i === 1 ? 'tomorrow' : nextDay} at ${nextDayHours.open}`
          };
        }
      }
      return { isOpen: false, message: 'Closed' };
    }

    // Parse open and close times
    const [openHour, openMin] = todayHours.open.split(':').map(Number);
    const [closeHour, closeMin] = todayHours.close.split(':').map(Number);
    const openTime = openHour * 60 + openMin;
    const closeTime = closeHour * 60 + closeMin;

    if (currentTime >= openTime && currentTime < closeTime) {
      return {
        isOpen: true,
        message: `Closing at ${todayHours.close}`
      };
    } else if (currentTime < openTime) {
      return {
        isOpen: false,
        message: `Opening at ${todayHours.open}`
      };
    } else {
      // After closing, find next open time
      for (let i = 1; i <= 7; i++) {
        const nextDayIndex = (now.getDay() + i) % 7;
        const nextDay = days[nextDayIndex];
        const nextDayHours = businessHours[nextDay];

        if (nextDayHours && !nextDayHours.closed) {
          return {
            isOpen: false,
            message: `Opening ${i === 1 ? 'tomorrow' : nextDay} at ${nextDayHours.open}`
          };
        }
      }
      return { isOpen: false, message: 'Closed' };
    }
  }

  /**
   * Update accepting orders status
   */
  async function updateAcceptingOrders() {
    const settings = await fetchSettings();
    if (!settings) return;

    const statusElements = document.querySelectorAll('[data-accepting-orders]');
    statusElements.forEach(element => {
      if (settings.accepting_orders) {
        element.classList.remove('orders-closed');
        element.classList.add('orders-open');
        element.textContent = element.dataset.openText || 'We are accepting orders';
      } else {
        element.classList.remove('orders-open');
        element.classList.add('orders-closed');
        element.textContent = element.dataset.closedText || 'We are currently not accepting orders';
      }
    });
  }

  /**
   * Update business hours status with opening/closing times
   */
  async function updateBusinessHoursStatus() {
    const settings = await fetchSettings();
    if (!settings || !settings.business_hours) return;

    const hoursStatus = getBusinessHoursStatus(settings.business_hours);
    if (!hoursStatus) return;

    // Update status badge
    const statusElements = document.querySelectorAll('[data-hours-status]');
    statusElements.forEach(element => {
      if (hoursStatus.isOpen) {
        element.classList.remove('status-closed');
        element.classList.add('status-open');
        element.textContent = element.dataset.openText || 'Open Now';
      } else {
        element.classList.remove('status-open');
        element.classList.add('status-closed');
        element.textContent = 'Closed';
      }
    });

    // Update opening/closing time
    const timeElements = document.querySelectorAll('[data-hours-time]');
    timeElements.forEach(element => {
      element.textContent = hoursStatus.message;
    });
  }

  /**
   * Update delivery information
   */
  async function updateDeliveryInfo() {
    const settings = await fetchSettings();
    if (!settings || !settings.delivery_info) return;

    // Update delivery time
    const timeElements = document.querySelectorAll('[data-delivery-time]');
    timeElements.forEach(el => {
      el.textContent = settings.delivery_info.estimated_time || '30-45 minutes';
    });

    // Update minimum order
    const minOrderElements = document.querySelectorAll('[data-min-order]');
    minOrderElements.forEach(el => {
      el.textContent = '£' + (settings.delivery_info.minimum_order || 15);
    });

    // Update free delivery threshold
    const freeDeliveryElements = document.querySelectorAll('[data-free-delivery-threshold]');
    freeDeliveryElements.forEach(el => {
      el.textContent = settings.delivery_info.free_delivery_threshold || 29;
    });
  }

  /**
   * Update delivery zones
   */
  async function updateDeliveryZones() {
    const settings = await fetchSettings();
    if (!settings || !settings.delivery_zones) return;

    const zonesContainer = document.getElementById('delivery-zones-display');
    if (!zonesContainer) return;

    // Skip if eats-home zone cards (detailed tiered pricing is hardcoded in HTML)
    if (zonesContainer.classList.contains('eats-delivery-info__zone-cards')) return;

    // Check if we're in eats-home (different styling)
    const isEatsHome = zonesContainer.classList.contains('eats-delivery-info__zone-list');

    let zonesHTML = '';
    settings.delivery_zones.forEach(zone => {
      if (isEatsHome) {
        // eats-home specific styling
        zonesHTML += `
          <div class="eats-delivery-info__zone">
            <span class="eats-delivery-info__zone-range">${zone.name}</span>
            <span class="eats-delivery-info__zone-price">£${zone.rate.toFixed(2)}</span>
          </div>
        `;
      } else {
        // Generic styling for other pages
        zonesHTML += `
          <div class="delivery-zone">
            <div class="delivery-zone__header">
              <span class="delivery-zone__name">${zone.name}</span>
              <span class="delivery-zone__rate">£${zone.rate.toFixed(2)}</span>
            </div>
            <div class="delivery-zone__postcodes">${zone.postcodes}</div>
          </div>
        `;
      }
    });

    zonesContainer.innerHTML = zonesHTML;
  }

  /**
   * Update promotional banner
   */
  async function updatePromotionalBanner() {
    const settings = await fetchSettings();
    if (!settings || !settings.promotional_banner) return;

    const bannerContainer = document.getElementById('promotional-banner');
    if (!bannerContainer) return;

    if (settings.promotional_banner.enabled && settings.promotional_banner.message) {
      const typeClass = `banner--${settings.promotional_banner.type || 'info'}`;
      bannerContainer.className = `promotional-banner ${typeClass}`;
      bannerContainer.innerHTML = `
        <div class="promotional-banner__content">
          <span class="promotional-banner__message">${settings.promotional_banner.message}</span>
          <button class="promotional-banner__close" onclick="this.parentElement.parentElement.style.display='none'">×</button>
        </div>
      `;
      bannerContainer.style.display = 'block';
    } else {
      bannerContainer.style.display = 'none';
    }
  }

  /**
   * Update menu promotional banner (compact)
   */
  async function updateMenuPromotionalBanner() {
    const menuBanner = document.getElementById('menu-promotional-banner');
    if (!menuBanner) return;

    // Fetch settings (will use cache if available)
    const settings = await fetchSettings();

    // Don't do anything if settings haven't loaded yet (avoids flash on first load)
    if (!settings) return;

    if (!settings.promotional_banner) {
      // Hide banner if no promotional_banner settings
      menuBanner.className = 'eatsmenu__promo-banner';
      menuBanner.textContent = '';
      menuBanner.removeAttribute('data-text');
      return;
    }

    if (settings.promotional_banner.enabled && settings.promotional_banner.message) {
      const typeClass = `banner--${settings.promotional_banner.type || 'info'}`;
      const message = settings.promotional_banner.message;

      // Set the text content and make visible immediately
      menuBanner.className = `eatsmenu__promo-banner active ${typeClass}`;
      menuBanner.textContent = message;

      // Check if text is overflowing and add scrolling animation
      requestAnimationFrame(() => {
        const isOverflowing = menuBanner.scrollWidth > menuBanner.clientWidth;
        if (isOverflowing) {
          // Add data attribute for CSS pseudo-elements and scrolling class
          menuBanner.setAttribute('data-text', message);
          menuBanner.classList.add('scrolling');
          menuBanner.textContent = ''; // Clear text since we're using ::before/::after
        }
      });
    } else {
      // Banner is disabled
      menuBanner.className = 'eatsmenu__promo-banner';
      menuBanner.textContent = '';
      menuBanner.removeAttribute('data-text');
    }
  }

  /**
   * Update contact information
   */
  async function updateContactInfo() {
    const settings = await fetchSettings();
    if (!settings || !settings.contact_info) return;

    // Update phone
    const phoneElements = document.querySelectorAll('[data-contact-phone]');
    phoneElements.forEach(el => {
      el.textContent = settings.contact_info.phone || '';
      if (el.tagName === 'A') {
        el.href = 'tel:' + settings.contact_info.phone;
      }
    });

    // Update WhatsApp links for catering and trailer
    const whatsappLinks = document.querySelectorAll('[data-catering-whatsapp-link], [data-trailer-whatsapp-link]');
    if (settings.contact_info.phone) {
      const cleanPhone = settings.contact_info.phone.replace(/\D/g, '');
      whatsappLinks.forEach(link => {
        const isTrailer = link.hasAttribute('data-trailer-whatsapp-link');
        const message = isTrailer
          ? encodeURIComponent('Hi! I\'d like to book the Spicy Delish food trailer for an event.')
          : '';
        link.href = `https://wa.me/${cleanPhone}${isTrailer ? '?text=' + message : ''}`;
      });
    }

    // Update email
    const emailElements = document.querySelectorAll('[data-contact-email]');
    emailElements.forEach(el => {
      el.textContent = settings.contact_info.email || '';
      if (el.tagName === 'A') {
        el.href = 'mailto:' + settings.contact_info.email;
      }
    });

    // Update address
    const addressElements = document.querySelectorAll('[data-contact-address]');
    addressElements.forEach(el => {
      el.textContent = settings.contact_info.address || '';
    });
  }

  /**
   * Initialize all dynamic settings
   */
  async function initializeSettings() {
    await Promise.all([
      updateBusinessHours(),
      updateAcceptingOrders(),
      updateBusinessHoursStatus(),
      updateDeliveryInfo(),
      updateDeliveryZones(),
      updatePromotionalBanner(),
      updateMenuPromotionalBanner(),
      updateContactInfo()
    ]);
  }

  /**
   * Get current settings (for use by other scripts)
   */
  window.getRestaurantSettings = async function() {
    return await fetchSettings();
  };

  /**
   * Refresh settings (clear all caches and reload from server)
   */
  window.refreshRestaurantSettings = async function() {
    cachedSettings = null;
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(LAST_UPDATED_KEY);
    await initializeSettings();
  };

  /**
   * Clear localStorage cache only (for debugging)
   */
  window.clearRestaurantSettingsCache = function() {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(LAST_UPDATED_KEY);
  };

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeSettings);
  } else {
    initializeSettings();
  }

})();
